import { useEffect, useMemo, useState } from "react";
import { FaRedo } from "react-icons/fa";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import InstallationSubpageLayout from "./installation/InstallationSubpageLayout";
import {
  formatRotationAngle,
  getCameraForSide,
  getRecentRawImageUrl,
  INSTALLATION_DETAIL_STALE_KEY,
  INSTALLATION_LIST_STALE_KEY,
  isInstallationSide,
} from "./installation/installationConfigUtils";
import {
  areRotationAnglesInSync,
  computeRotationPreviewDelta,
  normalizeRotationAngle,
} from "./installation/installationRotationUtils";
import { updateCameraRotation } from "./installation/installationApi";
import { useInstallationCamera } from "./installation/useInstallationCamera";
import { useInstallationImageRotation } from "./installation/useInstallationImageRotation";
import { useInstallationSideLabels } from "./installation/installationSideUtils";

const AppInstallationRotation = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id, side } = useParams();
  const { getRotationPageTitle } = useInstallationSideLabels();
  const [savedTargetAngle, setSavedTargetAngle] = useState(0);
  const [draftTargetAngle, setDraftTargetAngle] = useState(0);
  const [saving, setSaving] = useState(false);

  const { loading, leftCamera, rightCamera, cycleEntries, api, token, reload } =
    useInstallationCamera(id);

  const validSide = isInstallationSide(side) ? side : "left";
  const camera = getCameraForSide(leftCamera, rightCamera, validSide);
  const pageTitle = getRotationPageTitle(validSide);

  const { appliedAngle, appliedMatched, monitoringLoading } =
    useInstallationImageRotation({
      routeCameraId: id,
      side: validSide,
      cycleEntries,
      camera,
    });

  const imageUrl = useMemo(
    () => getRecentRawImageUrl(id, cycleEntries, validSide),
    [id, cycleEntries, validSide],
  );

  useEffect(() => {
    const angle = normalizeRotationAngle(camera?.image_rotation_angle ?? 0);
    setSavedTargetAngle(angle);
    setDraftTargetAngle(angle);
  }, [camera?.image_rotation_angle]);

  if (!isInstallationSide(side)) {
    return <Redirect to={`/app/installationdetails/${id}`} />;
  }

  const isDirty = draftTargetAngle !== savedTargetAngle;
  const previewDelta = computeRotationPreviewDelta(
    appliedAngle,
    draftTargetAngle,
  );
  const draftInSyncWithApplied = areRotationAnglesInSync(
    appliedAngle,
    draftTargetAngle,
  );

  const rotateDraft = () => {
    setDraftTargetAngle((current) => (current + 90) % 360);
  };

  const dismiss = () => {
    setDraftTargetAngle(savedTargetAngle);
    history.goBack();
  };

  const handleSave = async () => {
    if (!camera?._id || !isDirty || saving) {
      return;
    }

    setSaving(true);
    try {
      await updateCameraRotation(
        api,
        token,
        camera._id,
        draftTargetAngle,
        validSide,
      );
      sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
      sessionStorage.setItem(INSTALLATION_DETAIL_STALE_KEY, "1");
      toast.success(t("installation_saved"));
      await reload({ silent: true });
      history.goBack();
    } catch {
      toast.error(t("installation_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const headerAction = isDirty ? (
    <button
      type="button"
      className="installation-subpage-nav-button installation-subpage-nav-button-save"
      onClick={() => void handleSave()}
      disabled={saving || !camera?._id}
    >
      {saving ? t("installation_saving") : t("installation_save")}
    </button>
  ) : null;

  return (
    <InstallationSubpageLayout
      pageTitle={pageTitle}
      onBack={dismiss}
      setCookies={setCookies}
      saveUser={saveUser}
      headerAction={headerAction}
    >
      {loading && !camera && (
        <div className="installation-detail-state">
          <div className="installation-detail-spinner" aria-hidden />
          <p>{t("installation_loading")}</p>
        </div>
      )}

      {!loading && camera && (
        <>
          <section className="installation-subpage-card installation-rotation-metrics">
            <div className="installation-rotation-metric">
              <span className="installation-rotation-metric-label">
                {t("installation_rotation_applied_label")}
              </span>
              <span className="installation-rotation-metric-value">
                {monitoringLoading ? "…" : formatRotationAngle(appliedAngle)}
              </span>
            </div>
            <div className="installation-rotation-metric">
              <span className="installation-rotation-metric-label">
                {t("installation_rotation_target_label")}
              </span>
              <span className="installation-rotation-metric-value installation-rotation-metric-value-target">
                {formatRotationAngle(draftTargetAngle)}
              </span>
            </div>
          </section>

          {!monitoringLoading && !appliedMatched && (
            <p className="installation-rotation-notice" role="status">
              {t("installation_rotation_applied_unverified")}
            </p>
          )}

          {!monitoringLoading && appliedMatched && draftInSyncWithApplied && (
            <p
              className="installation-rotation-notice installation-rotation-notice-ok"
              role="status"
            >
              {t("installation_rotation_in_sync")}
            </p>
          )}

          {!monitoringLoading &&
            appliedMatched &&
            !draftInSyncWithApplied &&
            isDirty && (
              <p className="installation-rotation-notice" role="status">
                {t("installation_rotation_pending_next_cycle")}
              </p>
            )}

          <div className="installation-subpage-card installation-subpage-preview">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="installation-subpage-preview-img"
                style={{ transform: `rotate(${previewDelta}deg)` }}
              />
            ) : (
              <p className="installation-subpage-preview-empty">
                {t("installation_no_recent_image")}
              </p>
            )}

            <button
              type="button"
              className="installation-subpage-fab"
              onClick={rotateDraft}
              disabled={saving || !camera?._id}
              aria-label={t("installation_rotation_rotate_action")}
            >
              <FaRedo aria-hidden />
            </button>
          </div>

          <p className="installation-subpage-footnote">
            {t("installation_rotation_page_hint")}
          </p>
        </>
      )}
    </InstallationSubpageLayout>
  );
};

export default AppInstallationRotation;
